import { AlertCircle, Sparkles } from 'lucide-react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { AppInput } from '@/shared/components/ui/AppInput/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect/AppSelect';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { useMetaPixels } from '@/features/accounts/api/accounts-queries';
import type { ObjectiveBudgetFormValues } from '../../../../schemas/objective-budget.schema';
import { META_CONVERSION_EVENTS } from '../../../../schemas/objective-budget.schema';
import styles from './ConversionPixelFields.module.scss';

interface ConversionPixelFieldsProps {
  /**
   * React Hook Form instance from the parent step. We bind directly instead
   * of owning local state so the step's zod schema is the single source of
   * truth for validation.
   */
  form: UseFormReturn<ObjectiveBudgetFormValues>;
  adAccountId: string | undefined;
}

/**
 * Rendered inside the Objective step when the user picks a conversion
 * objective on Meta. Lets them select the Pixel, the event to optimize
 * for, and enter the HTTPS landing URL the ad clicks into.
 *
 * Hidden for non-conversion objectives so the wizard stays short.
 */
export function ConversionPixelFields({ form, adAccountId }: ConversionPixelFieldsProps) {
  const {
    control,
    register,
    formState: { errors },
  } = form;

  const { data: pixels, isLoading, isError, error } = useMetaPixels(adAccountId);

  const pixelOptions = (pixels ?? []).map((pixel) => ({
    value: pixel.id,
    label: pixel.lastFiredAt
      ? `${pixel.name} · last event ${formatLastFired(pixel.lastFiredAt)}`
      : `${pixel.name} · no events yet`,
  }));

  return (
    <AppCard className={styles.card}>
      <header className={styles.header}>
        <Sparkles size={18} aria-hidden />
        <div>
          <h3 className={styles.title}>Conversion tracking</h3>
          <p className={styles.description}>
            Meta optimizes delivery towards the Pixel event you pick here. Make sure the Pixel is
            installed on your landing page.
          </p>
        </div>
      </header>

      {!adAccountId && (
        <div className={styles.hint}>
          <AlertCircle size={14} aria-hidden />
          Select an ad account first to load available pixels.
        </div>
      )}

      {adAccountId && isLoading && (
        <div className={styles.hint}>
          <LoadingSpinner /> Loading pixels from Meta…
        </div>
      )}

      {adAccountId && isError && (
        <div className={styles.errorBanner}>
          <AlertCircle size={14} aria-hidden />
          {error instanceof Error ? error.message : "Couldn't load pixels — try again in a moment."}
        </div>
      )}

      {adAccountId && pixels && pixels.length === 0 && !isLoading && (
        <div className={styles.errorBanner}>
          <AlertCircle size={14} aria-hidden />
          This ad account has no pixels. Create one in Meta Events Manager before running a
          conversion campaign.
        </div>
      )}

      <div className={styles.grid}>
        <Controller
          control={control}
          name="pixelId"
          render={({ field }) => (
            <AppSelect
              label="Meta Pixel"
              placeholder="Select a pixel…"
              options={pixelOptions}
              value={field.value ?? ''}
              onValueChange={field.onChange}
              disabled={!adAccountId || pixelOptions.length === 0}
              error={errors.pixelId?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="conversionEvent"
          render={({ field }) => (
            <AppSelect
              label="Conversion event"
              placeholder="Select an event…"
              options={META_CONVERSION_EVENTS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={field.value ?? 'PURCHASE'}
              onValueChange={field.onChange}
              error={errors.conversionEvent?.message}
            />
          )}
        />
      </div>

      <AppInput
        label="Landing URL"
        placeholder="https://events.com/shows/..."
        {...register('landingUrl')}
        error={errors.landingUrl?.message}
      />
    </AppCard>
  );
}

function formatLastFired(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
